/**
 * Framework-agnostic HTTP envelope helpers — partagés SaaS ↔ widget.
 *
 * Le backend FerryGate (et le proxy Nitro qui le wrappe) répondent toujours
 * sous une enveloppe `{ status, data }` (succès) ou `{ status, error }`
 * (erreur). Ces fonctions PURES centralisent le parse / unwrap / extraction
 * d'erreur qui était auparavant dupliqué dans `apiClient.ts`, `booking.ts`,
 * `useFerryAvailabilityWidget.ts`, `useBookingActions.ts`, `useTicketCancel.ts`
 * et `widget/src/api.ts`.
 *
 * AUCUNE dépendance Pinia / Nitro / Nuxt / Vue — uniquement du JS pur.
 */

/**
 * Parse tolérant d'une string brute. Retourne `null` si vide, l'objet/valeur
 * parsé si JSON valide, sinon la string brute telle quelle (jamais throw).
 * Équivaut au parse de `apiClient.ts` (raw → JSON.parse, fallback raw) et de
 * `api.ts:parseJsonOrThrow` (mais sans throw — voir `parseJsonStrict`).
 */
export function parseJsonSafe(raw: string): unknown {
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

/**
 * Variante stricte : throw si la string n'est pas du JSON valide (et non vide).
 * Reproduit le comportement de `api.ts:parseJsonOrThrow` pour les call sites
 * qui veulent rejeter un body non-JSON plutôt que de le propager brut.
 */
export function parseJsonStrict(raw: string, onError: () => never): unknown {
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return onError()
  }
}

/**
 * Déballe une enveloppe `{ status, data }` → `data`. Si la valeur parsée n'est
 * pas une enveloppe (pas d'objet, ou pas de champ `data`), elle est retournée
 * telle quelle. Équivaut à `apiClient.ts` (`response.data`), `booking.ts`
 * (`response.data`), `api.ts:unwrapData` et aux 3 unwraps de
 * `useFerryAvailabilityWidget.ts`.
 */
export function unwrapEnvelope<T>(parsed: unknown): T {
  if (parsed !== null && typeof parsed === 'object' && 'data' in parsed) {
    return (parsed as { data: T }).data
  }
  return parsed as T
}

/**
 * Forme normalisée d'une erreur backend extraite d'un body de réponse.
 *
 * - `message` : message lisible (priorité `error.message`).
 * - `code`    : code d'erreur backend (`error.code` / top-level `code`).
 * - `detail`  : détail long (priorité `error.details.detail`).
 * - `title`   : titre court (`error.details.title`) — utilisé par les bannières UI.
 */
export interface UpstreamError {
  message?: string
  code?: string
  detail?: string
  title?: string
  /** Identifiant de corrélation (`X-Request-Id`) à citer au support. */
  requestId?: string
  /** Clé i18n d'un message lisible pour les codes techniques (`P005`…), sinon absente. */
  friendlyKey?: string
  /** `true` si réessayer a un sens (indisponibilité transitoire, conflit de concurrence). */
  retryable?: boolean
}

/**
 * Codes backend techniques dont le message brut est inutilisable à l'écran.
 * `P006` (refus compagnie) et `B001` (validation métier) n'y sont volontairement
 * pas : leur `detail` explique la raison et doit être affiché tel quel.
 */
const FRIENDLY_CODES: Record<string, { key: string, retryable: boolean }> = {
  P001: { key: 'errors.provider_unavailable', retryable: true },
  P002: { key: 'errors.provider_unavailable', retryable: true },
  P005: { key: 'errors.provider_unavailable', retryable: true },
  B003: { key: 'errors.price_changed', retryable: false },
  PRICE_CHANGED: { key: 'errors.price_changed', retryable: false },
  B004: { key: 'errors.conflict', retryable: true },
  SEC004: { key: 'errors.too_many_requests', retryable: true },
}

function asRecord(v: unknown): Record<string, unknown> | undefined {
  return v !== null && typeof v === 'object' ? (v as Record<string, unknown>) : undefined
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined
}

/**
 * Lit toutes les shapes d'erreur que le backend FerryGate / le proxy Nitro
 * peuvent produire et les normalise en `{ message, code, detail, title }`.
 *
 * Shapes couvertes (union de tous les call sites historiques) :
 *   - `error.message`                        (apiClient, booking, api.ts)
 *   - `error.detail`                         (apiClient, api.ts, normaliseError)
 *   - `error.code`                           (booking, api.ts)
 *   - `error.details.title`                  (booking, normaliseError)
 *   - `error.details.detail`                 (normaliseError, useTicketCancel)
 *   - `error.details.original_message`       (normaliseError)
 *   - `message` (top-level)                  (apiClient, api.ts)
 *   - `detail` (top-level)                   (api.ts)
 *   - `code` (top-level)                     (api.ts)
 *
 * Renvoie un objet potentiellement partiel — chaque champ vaut `undefined`
 * quand la shape correspondante est absente. Les call sites appliquent ensuite
 * leur propre ordre de fallback vers leurs shapes UI.
 */
export function extractUpstreamError(body: unknown): UpstreamError {
  const root = asRecord(body)
  if (!root) return {}

  const error = asRecord(root.error)
  const details = error ? asRecord(error.details) : undefined

  const message = str(error?.message) ?? str(root.message)
  const code = str(error?.code) ?? str(root.code)
  const detail =
    str(details?.detail)
    ?? str(details?.original_message)
    ?? str(error?.detail)
    ?? str(root.detail)
  const title = str(details?.title)
  const requestId = str(details?.request_id) ?? str(root.request_id)
  // `PRICE_CHANGED` voyage dans `details.code` ou `error.code` selon le provider.
  const friendly = FRIENDLY_CODES[code ?? ''] ?? FRIENDLY_CODES[str(details?.code) ?? '']

  return { message, code, detail, title, requestId, friendlyKey: friendly?.key, retryable: friendly?.retryable }
}
